// Curated city × material combinations for SEO landing pages.
// Each entry powers a /gravel-delivery/:citySlug/:materialSlug route via
// pages/CityMaterial.tsx. The list mixes King and Pierce County cities so
// the index page shows our full service footprint at a glance, not just
// the southern corridor.
import { categories } from './products'
export type ServiceCity = {
  slug: string
  name: string
  state: string
  county: 'King' | 'Pierce'
  zone: number
  // Display address used to prefill the estimator and seed the
  // Nominatim geocode on the destination page.
  geocodeHint: string
  neighborhoods: string[]
  // Common short trip notes — used in the page intro paragraph.
  blurb: string
}
export const SERVICE_CITIES: ServiceCity[] = [
  {
    slug: 'tacoma',
    name: 'Tacoma',
    state: 'WA',
    county: 'Pierce',
    zone: 2,
    geocodeHint: 'Tacoma, WA',
    neighborhoods: [
      'North End',
      'Stadium District',
      'Hilltop',
      'South Tacoma',
      'Browns Point',
      'University Place',
    ],
    blurb:
      'Tacoma is our home base. Most loads dispatch within 2 hours of order during business hours.',
  },
  {
    slug: 'seattle',
    name: 'Seattle',
    state: 'WA',
    county: 'King',
    zone: 3,
    geocodeHint: 'Seattle, WA',
    neighborhoods: [
      'West Seattle',
      'Beacon Hill',
      'Rainier Valley',
      'Georgetown',
      'Magnolia',
      'Queen Anne',
    ],
    blurb:
      'We deliver into Seattle daily. South-end neighborhoods route fastest; north of the ship canal adds an hour to dispatch.',
  },
  {
    slug: 'puyallup',
    name: 'Puyallup',
    state: 'WA',
    county: 'Pierce',
    zone: 2,
    geocodeHint: 'Puyallup, WA',
    neighborhoods: [
      'South Hill',
      'Riverside',
      'North Puyallup',
      'Edgerton',
      'Glacier View',
    ],
    blurb:
      'Puyallup is one of our busiest delivery corridors — same-day delivery is common for orders placed before 2pm.',
  },
  {
    slug: 'bellevue',
    name: 'Bellevue',
    state: 'WA',
    county: 'King',
    zone: 3,
    geocodeHint: 'Bellevue, WA',
    neighborhoods: [
      'Downtown',
      'Bridle Trails',
      'Somerset',
      'Newport',
      'Lake Hills',
      'Wilburton',
    ],
    blurb:
      'Bellevue routes off I-405 — solid lead times for both Eastside residential and commercial sites.',
  },
  {
    slug: 'kent',
    name: 'Kent',
    state: 'WA',
    county: 'King',
    zone: 1,
    geocodeHint: 'Kent, WA',
    neighborhoods: [
      'East Hill',
      'West Hill',
      'Panther Lake',
      'Meridian',
      'Lake Meridian',
    ],
    blurb:
      'Kent is in our innermost delivery zone — fastest routes from our dispatch yard.',
  },
  {
    slug: 'renton',
    name: 'Renton',
    state: 'WA',
    county: 'King',
    zone: 1,
    geocodeHint: 'Renton, WA',
    neighborhoods: [
      'Highlands',
      'Kennydale',
      'Talbot Hill',
      'The Landing',
      'Fairwood',
    ],
    blurb:
      'Renton is right next door to our dispatch yard — most loads roll within an hour of confirmation.',
  },
  {
    slug: 'auburn',
    name: 'Auburn',
    state: 'WA',
    county: 'King',
    zone: 2,
    geocodeHint: 'Auburn, WA',
    neighborhoods: [
      'Lea Hill',
      'West Hill',
      'Lakeland Hills',
      'Downtown Auburn',
    ],
    blurb:
      'Auburn deliveries route directly off 167 — quick turnarounds for both residential and contractor jobsites.',
  },
  {
    slug: 'federal-way',
    name: 'Federal Way',
    state: 'WA',
    county: 'King',
    zone: 2,
    geocodeHint: 'Federal Way, WA',
    neighborhoods: [
      'Twin Lakes',
      'Mirror Lake',
      'Lakota',
      'Redondo',
      'Brigadoon',
    ],
    blurb:
      'Federal Way sits in our core delivery zone with reliable same-week scheduling.',
  },
  {
    slug: 'redmond',
    name: 'Redmond',
    state: 'WA',
    county: 'King',
    zone: 3,
    geocodeHint: 'Redmond, WA',
    neighborhoods: [
      'Education Hill',
      'Grass Lawn',
      'Idylwood',
      'Overlake',
      'Bear Creek',
    ],
    blurb:
      'Redmond deliveries pair well with weekday morning slots — book ahead during peak landscape season.',
  },
  {
    slug: 'sammamish',
    name: 'Sammamish',
    state: 'WA',
    county: 'King',
    zone: 4,
    geocodeHint: 'Sammamish, WA',
    neighborhoods: [
      'Klahanie',
      'Pine Lake',
      'Trossachs',
      'Beaver Lake',
      'Inglewood',
    ],
    blurb:
      'Sammamish sits at our outer Eastside reach — schedule a day or two ahead for best timing.',
  },
]
// Featured products to generate combo pages for. Only top sellers — long-tail
// products still get organic SEO via their /shop/:slug product pages.
export const SERVICE_PRODUCT_SLUGS = [
  '5-8-minus', // 5/8" Minus
  '3-way-topsoil',
  '7-8-drain-rock', // 7/8" Drain Rock
  'medium-bark',
]
export function getServiceProduct(slug: string) {
  for (const cat of categories) {
    const p = cat.products.find((pp) => pp.slug === slug)
    if (p)
      return {
        product: p,
        category: cat,
      }
  }
  return null
}
export function getServiceCity(slug: string): ServiceCity | undefined {
  return SERVICE_CITIES.find((c) => c.slug === slug)
}
